// AI platforms and frameworks Tedmo builds with — shown on the Services page
// under the "AI Applications & Capabilities" service. Edit freely.

export const AI_STACK = [
  {
    key: "chatbots",
    label: "Chatbot Building & Deploying",
    intro:
      "Conversational agents for web, messaging apps, and internal tools — from visual builders to fully custom code.",
    tools: [
      { name: "Voiceflow", text: "Visual design platform for prototyping, managing agent logic, and collaborative building." },
      { name: "Botpress", text: "Developer-friendly cloud platform for LLM-native chatbots with custom JavaScript/TypeScript execution." },
      { name: "Langflow", text: "Low-code visual node builder for orchestrating LLM pipelines, prompt chains, and vector databases." },
      { name: "Flowise", text: "Open-source visual tool for building customized LLM applications using LangChain nodes." },
      { name: "Dify", text: "Full-stack LLM app platform covering prompt engineering, agent orchestration, and operational monitoring." },
      { name: "Stack AI", text: "Enterprise-grade no-code platform for injecting generative AI workflows into existing applications." },
      { name: "Coze", text: "Low-code platform by ByteDance for quickly building interactive AI bots and publishing them to messaging apps." },
      { name: "OpenAI Assistants API", text: "Developer framework with persistent threads, built-in code interpreter, and file retrieval for custom agents." },
      { name: "Dialogflow CX", text: "Google Cloud's platform for enterprise-grade, omnichannel conversational agents." },
      { name: "AWS Lex", text: "Managed Amazon service for building voice and text conversational interfaces into applications." },
    ],
  },
  {
    key: "automation",
    label: "Automation Workflows",
    intro:
      "Connect LLMs to the systems you already run — triggers, data pipelines, and autonomous multi-agent teams.",
    tools: [
      { name: "Make", text: "Visual workflow builder for passing data payloads and structuring JSON between LLMs and third-party APIs." },
      { name: "n8n", text: "Node-based, self-hostable workflow automation with dedicated native nodes for advanced AI integration." },
      { name: "Zapier Central", text: "AI-native workspace for training autonomous bots to execute multi-app tasks using natural language." },
      { name: "CrewAI", text: "Python framework for orchestrating role-based, autonomous AI agent teams on complex sequential tasks." },
      { name: "Microsoft AutoGen", text: "Framework for multi-agent conversation systems that act autonomously or with human input." },
      { name: "LangGraph", text: "Framework by LangChain for stateful, multi-actor applications with cyclical graph logic." },
      { name: "LlamaIndex", text: "Data framework connecting custom data sources to LLMs for automated ingestion and structured retrieval." },
      { name: "Relevance AI", text: "No-code platform for workflows driven by digital AI employees and data extraction." },
      { name: "Relay.app", text: "Modern workflow automation with built-in AI extraction steps and human-in-the-loop review checkpoints." },
      { name: "Activepieces", text: "Open-source, developer-focused Zapier alternative for self-hosting business and AI automation scripts." },
    ],
  },
  {
    key: "voice",
    label: "AI Voice Assistants",
    intro:
      "Real-time voice agents for phone lines and the web — low-latency speech, telephony, and natural conversation.",
    tools: [
      { name: "Vapi", text: "Voice-native platform with ultra-low latency pipelines for web and telephone voice bots." },
      { name: "Retell AI", text: "Conversational voice engine with deep developer APIs and native Twilio integration for phone conversations." },
      { name: "Bland AI", text: "High-scale enterprise infrastructure for sending and receiving automated phone-call agents." },
      { name: "LiveKit", text: "Open-source WebRTC streaming infrastructure powering real-time, bidirectional audio for voice assistants." },
      { name: "ElevenLabs", text: "Natural speech synthesis, voice cloning, and a conversational framework for deploying voice agents." },
      { name: "Hume AI", text: "Empathic voice interface that adapts responses to vocal modulation and emotional expression." },
      { name: "Cartesia", text: "High-speed real-time audio generation engine for streaming ultra-low latency voice responses." },
      { name: "Deepgram", text: "Fast Speech-to-Text transcription and Text-to-Speech delivery APIs for voice AI." },
      { name: "PlayHT", text: "Voice generation specializing in cloning and streaming humanlike conversational speech." },
      { name: "Twilio Media Streams", text: "Telephony infrastructure that pipes live phone-call audio directly into AI processing systems." },
    ],
  },
];

// Illustrative walkthrough of a typical AI assistant engagement (not a case
// study — no client is referenced). Shown on the Services page.
export const AI_WALKTHROUGH = {
  scenario:
    "Example: a customer-support assistant for a growing SMB that answers questions from its own help docs, order data, and policies — on the website and inside the team's helpdesk.",
  steps: [
    {
      name: "Discovery",
      duration: "Week 1",
      text: "We map the questions customers actually ask, the systems that hold the answers, and what 'good' looks like — accuracy, tone, escalation rules, and compliance needs.",
      deliverable: "Use-case brief, success metrics, and a data & systems inventory.",
    },
    {
      name: "Data & knowledge",
      duration: "Week 1–2",
      text: "Help articles, policies, and product data are cleaned, chunked, and indexed into a retrieval layer so the assistant answers from your facts — not from guesses.",
      deliverable: "A searchable knowledge base the assistant can cite.",
    },
    {
      name: "Prototype",
      duration: "Week 2–3",
      text: "A working assistant in a test channel. Your team talks to it daily; we tune prompts, retrieval, and model choice against real conversations.",
      deliverable: "Clickable prototype plus a transcript review log.",
    },
    {
      name: "Guardrails & evaluation",
      duration: "Week 3–4",
      text: "Automated test sets, refusal rules, PII handling, hand-off to a human when confidence is low, and cost/latency budgets — measured before launch, not after.",
      deliverable: "Evaluation report and a safety checklist you sign off on.",
    },
    {
      name: "Integrate & deploy",
      duration: "Week 4–5",
      text: "The assistant is wired into your website, helpdesk, or messaging channels and deployed to the cloud or your own servers with monitoring, logging, and access control.",
      deliverable: "Production launch on infrastructure you control.",
    },
    {
      name: "Monitor & improve",
      duration: "Ongoing",
      text: "We watch quality, usage, and cost, review unanswered questions, refresh the knowledge base, and ship improvements — the same long-term support as any system we build.",
      deliverable: "Monthly quality review and a steadily smarter assistant.",
    },
  ],
};
